import {Component} from 'preact';
import {SKU_PATTERN} from '../../../upi/constants';
import compose from 'ramda/src/compose';
import not from 'ramda/src/not';
import {Search} from '@ultradent/components';
import ItemProduct from '../list/ItemProduct';
import Item from '../list/Item';
import Icon from '@components/common/Icon';
import ProductCatalogService from '@services/ProductCatalogService';
import OrdersService from '@services/OrdersService';
import UpiContextProvider from '@providers/UpiContextProvider';

const skuRenderer = ( {sku, kitName, url} ) =>
    <ItemProduct as="a" sku={sku} longName={kitName} url={url}/>;

const itemRenderer = ( {brandName, url} ) => {
    return <Item as="a" href={url}>
        {brandName} <Icon icon="arrow-right" className="text-xl"/>
    </Item>;
};

function reduceProductList ( catalog ) {
    const map = new Map();
    catalog.forEach( ( {brandName, productFamily, searchKeywords, url} ) => {
        // filter any products that don't have a valid url (some catalogItems have null "url")
        if ( productFamily === 'Marketing Materials' || map.has( brandName ) ) {
            return;
        }
        if ( url ) {
            map.set( brandName, {brandName, productFamily, searchKeywords, url} )
        }
    } );

    return map;
}

const lblBuyAgain = upi.i18n.getTextFromKey( 'lbl-buyAgain' );
const wrdProducts = upi.i18n.getTextFromKey( 'wrd-Products' );
const wrdSKUs = upi.i18n.getTextFromKey( 'wrd-SKUs' );
const wrdSamples = upi.i18n.getTextFromKey( 'wrd-Samples' );
const lblMarketingMaterials = upi.i18n.getTextFromKey( 'lbl-marketingMaterials' );

const isMarketingMaterial = item => item.productFamily === 'Marketing Materials';
const isSample = item => /^s\d+/i.test( item.sku );
const isNotMarketingMaterial = compose( not, isMarketingMaterial );
const isNotSample = compose( not, isSample );
const excludeSkus = skuList => item => skuList.indexOf( item.sku ) === -1;

function buildQueryGroups ( catalog, pastPurchases, showBrandFamily, showAllSkus ) {
    // refactor for readability
    const pastPurchasesSkus = pastPurchases.map( i => i.sku );
    let queryGroups = [
        {
            groupName: lblBuyAgain,
            list: pastPurchases,
            queryProps: ['sku', 'kitName', 'searchKeywords'],
            renderer: skuRenderer
        }
    ];

    if ( showBrandFamily ) {
        const products = reduceProductList( catalog );
        window.products = products;
        const productsSorted = Array.from( products.entries() )
            .map( i => i[1] )
            .sort( ( a, b ) => a.brandName.localeCompare( b.brandName ) );
        queryGroups = queryGroups.concat( [{
            groupName: wrdProducts,
            list: productsSorted,
            useStrictMatching: true,
            queryProps: ['brandName', 'searchKeywords'],
            filter: isNotMarketingMaterial,
            renderer: itemRenderer
        }] );
    }

    queryGroups = queryGroups.concat( [
        {
            groupName: wrdSKUs,
            list: catalog,
            queryProps: ['kitName', 'searchKeywords'],
            renderer: skuRenderer,
            useStrictMatching: true,
            filter: [excludeSkus( pastPurchasesSkus ), isNotMarketingMaterial, isNotSample],
            hideIf: ( query, resultList ) =>
                SKU_PATTERN.test( query ) || (showBrandFamily && resultList[1].length > 5)
        },
        {
            groupName: wrdSKUs,
            list: catalog,
            queryProps: ['sku'],
            renderer: skuRenderer,
            useStrictMatching: true,
            filter: [excludeSkus( pastPurchasesSkus ), isNotMarketingMaterial, isNotSample],
            hideIf: ( query, resultList ) =>
                showBrandFamily && !SKU_PATTERN.test( query ) && resultList[1].length > 5
        }
    ] );

    if ( showAllSkus ) {
        queryGroups = queryGroups.concat( [
            {
                groupName: wrdSamples,
                list: catalog,
                queryProps: ['sku', 'kitName', 'searchKeywords'],
                renderer: skuRenderer,
                filter: isSample
            }
        ] );
        queryGroups = queryGroups.concat( [
            {
                groupName: lblMarketingMaterials,
                list: catalog,
                queryProps: ['sku', 'kitName', 'searchKeywords', 'productFamily'],
                renderer: skuRenderer,
                filter: isMarketingMaterial
            }
        ] );
    }

    return queryGroups;
}

class ProductSearch extends Component {

    static Results = Search.Results;
    static ResultsPane = Search.ResultsPane;
    static ResultItem = Search.ResultItem;
    static Input = Search.Input;

    state = {
        loading: true,
        catalog: null,
        pastPurchases: null
    };

    componentWillMount () {
        const {pastPurchases} = this.props;
        const getCatalog = ProductCatalogService.getAll();
        const getRecentPurchases = new Promise( ( resolve ) => {

            if ( pastPurchases ) {
                return resolve( pastPurchases );
            }

            return OrdersService.getPastPurchases( 32 ).then(
                data => resolve( data ),
                () => resolve( [] )
            );
        } );

        Promise.all( [getCatalog, getRecentPurchases] ).then( ( [catalog, pastPurchases] ) => {
            this.setState( {catalog, pastPurchases, loading: false} );
        } );
    }

    onResultSelect = ( evt, result ) => {
        if ( evt.which === 13 ) {
            const el = $( `#search-result-item-${result.index} > a` );
            switch ( result.queryGroup.groupName ) {
                case (lblBuyAgain):
                case (wrdSKUs):
                    el.click();
                    break;

                default:
                    window.location = el.attr( 'href' );

            }
        }
    };

    render ( props, context = {} ) {
        const {catalog, pastPurchases} = this.state;
        const {showBrandFamily, showAllSkus} = this.props;
        const queryGroups = !this.state.loading
            ? buildQueryGroups( catalog, pastPurchases, showBrandFamily, showAllSkus )
            : [];

        return (
            <UpiContextProvider {...context}>
                <Search
                    ignoreSelectedIndex={true}
                    initialQuery={props.initialQuery}
                    onResultSelect={this.onResultSelect}
                    hideOnFocusout={true}
                    queryGroup={queryGroups}>
                    {props.children}
                </Search>
            </UpiContextProvider>
        )
    }
}

export default ProductSearch;
